import { type Dispatch } from '@reduxjs/toolkit'
import axios from '../Config/AxiosConfig'
import { setLoadingStatus } from '../Reducer/Common'
import { SuccessMessage, ErrorMessage } from '../Utils/Notification'

export const uploadFileToServer = async (
    dispatch: Dispatch,
    file: File,
    dynamicPath: string,
    messageShowId?: number
): Promise<any> => {
    const formData = new FormData()
    formData.append('file', file)
    dispatch(setLoadingStatus({ status: true }))
    try {
        const response = await axios.post(
            `api/File/UploadFile?path=${dynamicPath}`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Accept: 'application/json',
                },
            }
        )
        if (response.data.statusCode === 200) {
            if (messageShowId === undefined) {
                SuccessMessage(response?.data?.operationMsg)
            }
            dispatch(setLoadingStatus({ status: false }))
            return response?.data?.data?.fileUrl
        } else {
            if (messageShowId === undefined) {
                ErrorMessage(
                    response?.data?.operationMsg ?? 'File upload failed'
                )
            }
            dispatch(setLoadingStatus({ status: false }))
            return null
        }
    } catch (error: any) {
        console.error('Upload error:', error)
        if (messageShowId === undefined) {
            ErrorMessage(
                error?.response?.data?.message ??
                    'An unexpected error occurred during file upload'
            )
        }
        dispatch(setLoadingStatus({ status: false }))
        return null
    }
}

export const uploadMultipleFileToServer = async (
    dispatch: Dispatch,
    multipleFiles: File[],
    dynamicPath: string
): Promise<any> => {
    const formData = new FormData()
    multipleFiles.forEach((file) => {
        formData.append('files', file)
    })
    dispatch(setLoadingStatus({ status: true }))
    try {
        const response = await axios.post(
            `/api/File/UploadMultipleFiles?path=${dynamicPath}`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Accept: 'application/json',
                },
            }
        )
        if (response.data.statusCode === 200) {
            SuccessMessage(response?.data?.operationMsg)
            dispatch(setLoadingStatus({ status: false }))
            return response?.data?.data?.fileUrls
        } else {
            ErrorMessage(response?.data?.operationMsg ?? 'File upload failed')
            dispatch(setLoadingStatus({ status: false }))
            return null
        }
    } catch (error: any) {
        console.error('Upload error:', error)
        ErrorMessage(
            error?.response?.data?.message ??
                'An unexpected error occurred during file upload'
        )
        dispatch(setLoadingStatus({ status: false }))
        return null
    }
}

export const deleteFileFromServer = async (fileUrl: string): Promise<any> => {
    try {
        // const url = new URL(fileUrl)
        // const fileKey = url.pathname.substring(1) // remove leading slash

        const response = await axios.delete(`/api/File/DeleteFile`, {
            params: { fileKey: fileUrl },
        })
        if (response.data.statusCode === 200) {
            SuccessMessage(response?.data?.operationMsg)

            return true
        } else {
            ErrorMessage(response?.data?.operationMsg ?? 'File Delete failed')

            return null
        }
    } catch (error) {
        console.error('Failed to delete file:', error)
        return false
    }
}

export const downloadFileFromServer = async (
    fileUrl: string
): Promise<void> => {
    const url = new URL(fileUrl)
    const fileKeys = url.pathname?.toString().replace(/^\/+/, '')

    try {
        const requestUrl = `/api/File/DownloadFile?fileKey=${fileKeys}`
        const response = await axios.get(requestUrl, {
            responseType: 'blob', // important!
        })

        const blob = new Blob([response.data])
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)

        // Optional: extract filename from fileUrl
        const fileName = fileUrl.split('/').pop() ?? 'download'
        link.download = fileName

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    } catch (error) {
        console.error('Download error:', error)
    }
}

export const createPreSignedUrl = async (fileKey: string): Promise<string> => {
    const url = new URL(fileKey)
    const fileKeys = url.pathname?.toString().replace(/^\/+/, '')

    try {
        const requestUrl = `/api/File/GetPresignedUrl?fileKey=${fileKeys}&expirationMinutes=60`

        const response = await axios.get(requestUrl) // no `params` — manually constructed

        if (response.data.statusCode === 200) {
            return response.data?.data?.presignedUrl ?? ''
        } else {
            ErrorMessage(
                response?.data?.operationMsg ?? 'Create Presigned URL Fail'
            )
            return ''
        }
    } catch (error) {
        console.error('Error fetching pre-signed URL:', error)
        return ''
    }
}
